import { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdOutlineAdd } from 'react-icons/md';
import { toast } from 'react-toastify';
import { Button, SearchInput } from '@ds';
import { AdminPageHeader, ConditionalRender, DataTable, DeleteModal } from '@ui';
import { AuthContext } from '@lp/core/auth';
import { OpoContactFilters } from '@lp/features/admin/tables/OpoContactListTable/components/OpoContactFilters';
import {
  getOpoContactsTableColumns,
  getRenderOpoContactsTableCell,
  OPO_CONTACT_LIST_DEFAULT_SORT_BY,
  OPO_CONTACT_LIST_FILTERS_BASE,
} from '@lp/features/admin/tables/OpoContactListTable';
import { useFilterQueryWithParams } from '@/hooks/useFilterQueryWithParams';
import { useEntityDelete } from '@/hooks/useEntityDelete';
import { AdminPermissions } from '@/permissions';
import { useOpoContactListQuery, useUpdateOpoContactMutation } from '@/queries/opo';
import { useWrappedRemoveOpoContactMutation } from '@/queries/orchestrator';
import { useResendUserInviteMutation } from '@/queries/user';
import { OpoContact } from '@/api/apis/opo';
import { retrieveUnifiedError } from '@/api/utils';

export const OpoContactsList = () => {
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isFetching,
    filters,
    hasFilters,
    updateFilters,
    clearFilters,
    page,
    itemsPerPage,
    searchText,
    sortBy,
    setPage,
    setItemsPerPage,
    setSearchText,
    setSortBy,
  } = useFilterQueryWithParams({
    filterList: OPO_CONTACT_LIST_FILTERS_BASE,
    query: useOpoContactListQuery,
    defaultSortBy: OPO_CONTACT_LIST_DEFAULT_SORT_BY,
  });

  const { checkPermission } = useContext(AuthContext);
  const hasCreatePermissions = checkPermission(AdminPermissions.OpoCreate);

  const { mutateAsync: resendInvite } = useResendUserInviteMutation();
  const { mutateAsync: updateOpoContact } = useUpdateOpoContactMutation();

  const {
    isOpen,
    isLoading: isDeletionLoading,
    onModalOpen,
    onModalClose,
    onEntityDelete,
  } = useEntityDelete({
    query: useWrappedRemoveOpoContactMutation,
    successMessage: 'OPO contact deleted successfully.',
  });

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchText(event.target.value);
  };

  const handleCreate = () => {
    navigate(`/admin/opos/contacts/create`);
  };

  const handleEdit = useCallback(
    (contactId: string) => {
      navigate(`/admin/opos/contacts/${contactId}`);
    },
    [navigate],
  );

  const handleResendInvite = useCallback(
    async (contact: OpoContact) => {
      if (!contact.userId) return;

      try {
        await resendInvite(contact.userId);
        toast.success('OPO user invite resent successfully.');
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [resendInvite],
  );

  const handleActiveChange = useCallback(
    async (contactId: string, isActive: boolean) => {
      try {
        await updateOpoContact({
          id: contactId,
          isActive,
        });

        toast.success(`OPO user account ${isActive ? 'activated' : 'deactivated'} successfully.`);
      } catch (e) {
        const error = retrieveUnifiedError(e);
        toast.error(error.message);
      }
    },
    [updateOpoContact],
  );

  const TABLE_COLUMNS = useMemo(() => getOpoContactsTableColumns(hasCreatePermissions), [hasCreatePermissions]);

  const renderTableCell = useMemo(
    () =>
      getRenderOpoContactsTableCell({
        onEdit: handleEdit,
        onDelete: onModalOpen,
        onResendInvite: handleResendInvite,
        onActiveChange: handleActiveChange,
      }),
    [handleEdit, onModalOpen, handleResendInvite, handleActiveChange],
  );

  const pageTitle = 'OPO Contacts';

  return (
    <>
      <div className="flex flex-col place-items-stretch gap-9">
        <AdminPageHeader title={pageTitle}>
          <div className="max-w-[480px] grow">
            <SearchInput placeholder="Search" value={searchText} onChange={onSearchChange} inputVariant="secondary" />
          </div>

          <ConditionalRender show={hasCreatePermissions}>
            <div className="shrink-0">
              <Button size="md" variant="solid" endContent={<MdOutlineAdd size={18} />} onPress={handleCreate}>
                Create
              </Button>
            </div>
          </ConditionalRender>
        </AdminPageHeader>

        <div className="flex flex-col gap-4">
          <OpoContactFilters
            filters={filters}
            hasFilters={hasFilters}
            updateFilters={updateFilters}
            clearFilters={clearFilters}
          />

          <DataTable
            label={pageTitle}
            columns={TABLE_COLUMNS}
            renderCell={renderTableCell}
            data={data?.items || []}
            isLoading={isLoading}
            isFetching={isFetching}
            totalItems={data?.totalItems || 0}
            totalPages={data?.totalPages || 1}
            page={page}
            itemsPerPage={itemsPerPage}
            sortBy={sortBy}
            onPageChange={setPage}
            onItemsPerPageChange={setItemsPerPage}
            onSortByChange={setSortBy}
          />
        </div>
      </div>

      <DeleteModal
        title="Delete OPO Contact?"
        description="Are you sure you want to delete the OPO Contact?"
        size="lg"
        isOpen={isOpen}
        isLoading={isDeletionLoading}
        onDelete={onEntityDelete}
        onClose={onModalClose}
      />
    </>
  );
};
